import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Oval } from "react-loader-spinner";

// Import API methods from helpers/requests
import {
  getTicketById,
  sendMessageInTicket,
  closeTicket,
  assignAdmin,
} from "helpers/requests";

const TicketDetails = (props) => {
  const { ticketId } = useParams(); // Get the ticket ID from the URL
  const [ticket, setTicket] = useState(null); // State to store ticket data
  const [newMessage, setNewMessage] = useState(""); // State for the new message
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const navigate = useNavigate(); // For navigation

  // Fetch ticket details from the API
  useEffect(() => {
    const fetchTicketDetails = async () => {
      const token = localStorage.getItem("auth");
      if (!token) {
        console.error("No token found");
        navigate("/login"); // Redirect to login if no token
        return;
      }

      try {
        const response = await getTicketById(ticketId); // Use getTicketById from helpers/requests
        setTicket(response); // Store the fetched ticket data
      } catch (error) {
        console.error("Error fetching ticket details:", error);
      } finally {
        setIsLoading(false); // Stop loading
      }
    };

    fetchTicketDetails();
  }, [ticketId, navigate]);

  // Handle sending a new message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return; // Don't send empty messages

    const token = localStorage.getItem("auth");
    if (!token) {
      console.error("No token found");
      navigate("/login"); // Redirect to login if no token
      return;
    }

    try {
        if(!ticket.adminUsername){
            const username = localStorage.getItem("username");
            await assignAdmin(ticket.id,username)
        }
      const messageData = {
        message: newMessage,
        senderType: "ADMIN", // Set senderType to USER
      };

      // Send the message to the backend
      await sendMessageInTicket(ticketId, messageData); // Use sendMessageInTicket from helpers/requests
      setNewMessage(""); // Clear the input field

      // Refresh the ticket data to show the new message
      const response = await getTicketById(ticketId);
      setTicket(response);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Handle closing the ticket
  const handleCloseTicket = async () => {
    const token = localStorage.getItem("auth");
    if (!token) {
      console.error("No token found");
      navigate("/login"); // Redirect to login if no token
      return;
    }

    try {
      await closeTicket(ticketId); // Use closeTicket from helpers/requests
      // Refresh the ticket data to show the updated status
      const response = await getTicketById(ticketId);
      setTicket(response);
    } catch (error) {
      console.error("Error closing ticket:", error);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <Oval
          height={80}
          width={80}
          color="#556ee6"
          visible={true}
          ariaLabel="oval-loading"
          secondaryColor="#4fa94d"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
      </div>
    );
  }

  if (!ticket) {
    return <div className="error-message">Ticket not found.</div>; // Show error if ticket doesn't exist
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <CardTitle className="mb-4" >Ticket Details</CardTitle>

                {/* Ticket Information */}
                <Row className="mb-4">
                  <Col md="6">
                    <FormGroup>
                      <Label>
                        <strong>Subject:</strong> {ticket.subject}
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>
                        <strong>Status:</strong>{" "}
                        <Badge
                          pill
                          className={`badge-soft-${
                            ticket.state === "OPEN" ? "danger" : "success"
                          } ms-1`}
                        >
                          {ticket.state}
                        </Badge>
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>
                        <strong>Admin In Charge:</strong>{" "}
                        {ticket.adminUsername || "Not assigned yet"}
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>
                        <strong>Created At:</strong>{" "}
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Messages Section */}
                <Row className="mb-4">
                  <Col lg="12">
                    <h5 style={{marginBottom:"60px"}}>Messages</h5>
                    <hr/>
                    <div className="d-flex flex-column gap-3">
                      {ticket.messages
                        .sort(
                          (a, b) =>
                            new Date(a.created_at) - new Date(b.created_at)
                        ) // Sort messages from old to new
                        .map((message) => (
                          <div
                            key={message.id}
                            className={`d-flex ${
                              message.senderType.toLowerCase() === "user"
                                ? "justify-content-start"
                                : "justify-content-end"
                            }`}
                          >
                            <div
                              className={`p-3 rounded ${
                                message.senderType.toLowerCase() === "user"
                                  ? "bg-light "
                                  : "bg-primary text-white"
                              }`}
                              style={{ maxWidth: "70%" }}
                            >
                              <p className="mb-0">{message.message}</p>
                              <small
                className={
                  message.senderType.toLowerCase() === "admin"
                    ? "text-white" // White for admin
                    : "text-muted" // Grey for user
                }
              >
                {new Date(message.created_at).toLocaleString()}
              </small>
                            </div>
                          </div>
                        ))}
                    </div>
                  </Col>
                </Row>

                {/* Message Input Section */}
                {ticket.state !== "CLOSED" && (
                  <Row className="mb-4">
                    <Col lg="12">
                      <h5>Send a Message</h5>
                      <FormGroup>
                        <Input
                          type="textarea"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          rows="4"
                        />
                      </FormGroup>
                      <Button
                        color="primary"
                        onClick={handleSendMessage}
                        className="me-2"
                      >
                        Send Message
                      </Button>
                      <Button
                        color="danger"
                        onClick={handleCloseTicket}
                      >
                        Close Ticket
                      </Button>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

TicketDetails.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(TicketDetails);